import { FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  cancelEmailOutbox,
  deleteEmailOutbox,
  EmailOutboxItem,
  listEmailOutbox,
  resendEmailOutbox,
} from '../../services/emailOutboxApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'Pending', label: 'Pendente' },
  { value: 'Processing', label: 'Processando' },
  { value: 'Sent', label: 'Enviado' },
  { value: 'Failed', label: 'Falhou' },
  { value: 'Cancelled', label: 'Cancelado' },
];

function statusLabel(status: string): string {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label ?? status;
}

function statusClass(status: string): string {
  switch (status) {
    case 'Sent':
      return 'bg-emerald-50 text-emerald-800';
    case 'Failed':
      return 'bg-red-50 text-red-800';
    case 'Cancelled':
      return 'bg-secondary-100 text-secondary-700';
    case 'Processing':
      return 'bg-sky-50 text-sky-800';
    default:
      return 'bg-amber-50 text-amber-900';
  }
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

export function EmailQueuePage() {
  const { hasPermission } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = hasPermission('settings.email-queue.write');

  const [items, setItems] = useState<EmailOutboxItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await listEmailOutbox({
        status: status || undefined,
        search: search || undefined,
        page,
        pageSize,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar a fila de e-mails.'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  function handleSearchSubmit(event: FormEvent): void {
    event.preventDefault();
    setPage(1);
    setSearch(searchDraft.trim());
  }

  async function handleResend(item: EmailOutboxItem): Promise<void> {
    const ok = await confirm({
      title: 'Reenviar e-mail',
      message: `Colocar novamente na fila o e-mail para ${item.toAddress}?`,
      confirmLabel: 'Reenviar',
    });
    if (!ok) return;

    setActingId(item.id);
    try {
      await resendEmailOutbox(item.id);
      toast.success('E-mail reenfileirado.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao reenviar e-mail.'));
    } finally {
      setActingId(null);
    }
  }

  async function handleCancel(item: EmailOutboxItem): Promise<void> {
    const ok = await confirm({
      title: 'Cancelar e-mail',
      message: `Cancelar o envio para ${item.toAddress}?`,
      confirmLabel: 'Cancelar envio',
    });
    if (!ok) return;

    setActingId(item.id);
    try {
      await cancelEmailOutbox(item.id);
      toast.success('E-mail cancelado.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao cancelar e-mail.'));
    } finally {
      setActingId(null);
    }
  }

  async function handleDelete(item: EmailOutboxItem): Promise<void> {
    const ok = await confirm({
      title: 'Excluir da fila',
      message: `Remover permanentemente o registro do e-mail para ${item.toAddress}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;

    setActingId(item.id);
    try {
      await deleteEmailOutbox(item.id);
      toast.success('Registro removido da fila.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir registro.'));
    } finally {
      setActingId(null);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Fila de e-mails"
        description="Acompanhe envios pendentes, falhas e histórico. Reenvie, cancele ou exclua registros desta organização."
      />

      <form
        onSubmit={handleSearchSubmit}
        className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border border-secondary-200 bg-white px-4 py-3"
      >
        <label className="erp-label min-w-[12rem]">
          Status
          <select
            className="erp-input"
            value={status}
            onChange={(event) => {
              setPage(1);
              setStatus(event.target.value);
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label min-w-[16rem] flex-1">
          Buscar
          <input
            className="erp-input"
            value={searchDraft}
            placeholder="Destinatário, assunto ou erro"
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <button type="submit" className="erp-btn-secondary">
          Filtrar
        </button>
      </form>

      {loading ? (
        <LoadingSpinner label="Carregando fila..." />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhum e-mail encontrado na fila.</p>
      ) : (
        <>
          <ul className="divide-y divide-secondary-100 rounded-xl border border-secondary-200 bg-white">
            {items.map((item) => {
              const busy = actingId === item.id;
              const canResend = item.status !== 'Processing';
              const canCancel =
                item.status === 'Pending' || item.status === 'Failed';
              const canDelete = item.status !== 'Processing';

              return (
                <li key={item.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-secondary-900">{item.subject || '(sem assunto)'}</p>
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                    </div>
                    <p className="text-sm text-secondary-700">{item.toAddress}</p>
                    <p className="text-xs text-secondary-500">
                      Criado {formatDateTime(item.createdAtUtc)}
                      {item.sentAtUtc ? ` · Enviado ${formatDateTime(item.sentAtUtc)}` : null}
                      {item.status === 'Pending'
                        ? ` · Próxima tentativa ${formatDateTime(item.nextAttemptAtUtc)}`
                        : null}
                      {' · '}
                      Tentativas {item.attemptCount}/{item.maxAttempts}
                      {item.emailTemplateName ? ` · Template: ${item.emailTemplateName}` : null}
                      {item.correlationType ? ` · ${item.correlationType}` : null}
                    </p>
                    {item.lastError ? (
                      <p className="text-xs text-red-700">{item.lastError}</p>
                    ) : null}
                  </div>
                  {canWrite ? (
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                      {canResend ? (
                        <button
                          type="button"
                          className="erp-btn-secondary px-2 py-1 text-xs"
                          disabled={busy}
                          onClick={() => void handleResend(item)}
                        >
                          Reenviar
                        </button>
                      ) : null}
                      {canCancel ? (
                        <button
                          type="button"
                          className="erp-btn-secondary px-2 py-1 text-xs"
                          disabled={busy}
                          onClick={() => void handleCancel(item)}
                        >
                          Cancelar
                        </button>
                      ) : null}
                      {canDelete ? (
                        <DeleteIconButton disabled={busy} onClick={() => void handleDelete(item)} />
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() =>
              setPage((current) =>
                Math.min(Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize))), current + 1)
              )
            }
            onPageSizeChange={(next) => {
              setPage(1);
              setPageSize(next);
            }}
          />
        </>
      )}
    </PageShell>
  );
}
